import React, { useEffect, useMemo, useRef, useState } from "react";
import { scaleLinear } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridColumns, GridRows } from "@visx/grid";
import type { NumberValue } from "d3-scale";

export type Breakpoint = { m: number; v: number };

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// Hit-test radius in pixels for detecting clicks on existing points
const HIT_R = 8;

const minutesToHM = (m: number): string => {
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

const defaultDay = (): Breakpoint[] => [
  { m: 0, v: 20 },
  { m: 420, v: 60 },
  { m: 540, v: 85 },
  { m: 720, v: 70 },
  { m: 960, v: 55 },
  { m: 1110, v: 45 },
  { m: 1260, v: 10 },
  { m: 1440, v: 0 },
];

const buildLineAndArea = (
  pts: Breakpoint[],
  xScale: (n: number) => number,
  yScale: (n: number) => number,
  baselineY: number
): { line: string; area: string } => {
  if (!pts.length) return { line: "", area: "" };
  const p = pts.slice().sort((a, b) => a.m - b.m);
  let line = `M ${xScale(p[0].m)} ${yScale(p[0].v)}`;
  for (let i = 1; i < p.length; i++) line += ` L ${xScale(p[i].m)} ${yScale(p[i].v)}`;

  let area = `M ${xScale(p[0].m)} ${baselineY} L ${xScale(p[0].m)} ${yScale(p[0].v)}`;
  for (let i = 1; i < p.length; i++) area += ` L ${xScale(p[i].m)} ${yScale(p[i].v)}`;
  area += ` L ${xScale(p[p.length - 1].m)} ${baselineY} Z`;
  return { line, area };
};

export const OccupancyPlanEditor = ({
  initialPoints,
  onChange,
  color = '#0ea5e9',
}: {
  initialPoints?: Breakpoint[];
  onChange?: (points: Breakpoint[]) => void;
  color?: string;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [svgWidth, setSvgWidth] = useState<number>(960);
  const [svgHeight, setSvgHeight] = useState<number>(500);

  const margin = { top: 24, right: 28, bottom: 48, left: 68 };
  const innerWidth = svgWidth - margin.left - margin.right;
  const innerHeight = svgHeight - margin.top - margin.bottom;

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => {
      const w = el.clientWidth || 960;
      const h = Math.max(360, Math.min(560, Math.round(w * 0.52)));
      setSvgWidth(w);
      setSvgHeight(h);
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [pointsData, setPointsData] = useState<Breakpoint[]>(() => {
    const src = initialPoints && initialPoints.length ? initialPoints : defaultDay();
    const sorted = src.slice().sort((a, b) => a.m - b.m);
    if (sorted[0].m !== 0) sorted.unshift({ m: 0, v: sorted[0].v ?? 0 });
    if (sorted[sorted.length - 1].m !== 1440) sorted.push({ m: 1440, v: sorted[sorted.length - 1].v ?? 0 });
    return sorted.map(p => ({ m: clamp(p.m, 0, 1440), v: clamp(p.v, 0, 100) }));
  });
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // react to external initialPoints updates (e.g., when user clicks "Редактировать")
  useEffect(() => {
    if (!initialPoints || initialPoints.length === 0) return;
    const sorted = initialPoints.slice().sort((a, b) => a.m - b.m);
    if (sorted[0].m !== 0) sorted.unshift({ m: 0, v: sorted[0].v ?? 0 });
    if (sorted[sorted.length - 1].m !== 1440) sorted.push({ m: 1440, v: sorted[sorted.length - 1].v ?? 0 });
    const normalized = sorted.map(p => ({ m: clamp(p.m, 0, 1440), v: clamp(p.v, 0, 100) }));
    setPointsData(normalized);
    setSelectedIdx(null);
    setHoverIdx(null);
    onChange?.(normalized);
  }, [initialPoints]);

  const xScale = useMemo(() => scaleLinear({ domain: [0, 1440], range: [0, innerWidth] }), [innerWidth]);
  const yScale = useMemo(() => scaleLinear({ domain: [0, 100], range: [innerHeight, 0] }), [innerHeight]);

  // Precompute hourly ticks for legend and grid (00:00 .. 24:00)
  const hourTicks = useMemo(() => Array.from({ length: 25 }, (_, i) => i * 60), []);

  const svgRef = useRef<SVGSVGElement | null>(null);

  const pathPoints = pointsData.map(d => ({ x: xScale(d.m), y: yScale(d.v) }));
  const { line, area } = useMemo(() => buildLineAndArea(pointsData, xScale, yScale, innerHeight), [pointsData, xScale, yScale, innerHeight]);

  // Helpers: local coordinates and hit-testing
  const getLocalXY = (clientX: number, clientY: number) => {
    const svg = svgRef.current!;
    const rect = svg.getBoundingClientRect();
    const px = clientX - rect.left - margin.left;
    const py = clientY - rect.top - margin.top;
    return { px, py };
  };

  const hitTestPoint = (px: number, py: number): number => {
    let bestIdx = -1;
    let bestDist2 = HIT_R * HIT_R;
    for (let i = 0; i < pathPoints.length; i++) {
      const dx = px - pathPoints[i].x;
      const dy = py - pathPoints[i].y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= bestDist2) {
        bestIdx = i;
        bestDist2 = d2;
      }
    }
    return bestIdx;
  };

  const insertPointAt = (m: number): Breakpoint[] => {
    // snap to 10-minute grid similar to dragging
    const snap = (val: number, step: number) => Math.round(val / step) * step;
    const mm = clamp(snap(m, 10), 1, 1439);
    // find segment [i, i+1]
    const pts = pointsData.slice();
    let i = 0;
    while (i < pts.length - 1 && pts[i + 1].m < mm) i++;
    // ensure i is left, right is i+1 where right.m >= mm
    while (i < pts.length - 1 && pts[i].m > mm) i--;
    i = clamp(i, 0, pts.length - 2);
    const left = pts[i];
    const right = pts[i + 1];
    const t = (mm - left.m) / (right.m - left.m);
    const v = clamp(left.v + t * (right.v - left.v), 0, 100);
    // avoid duplicate m
    const exists = pts.findIndex(p => Math.round(p.m) === Math.round(mm));
    if (exists !== -1) {
      return pts; // do nothing if same minute already exists
    }
    const next = [...pts, { m: mm, v }].sort((a, b) => a.m - b.m);
    return next;
  };

  const handleDblClick = (e: React.MouseEvent<SVGElement, MouseEvent>) => {
    e.preventDefault();
    const { px, py } = getLocalXY(e.clientX, e.clientY);
    // If dblclick is outside inner chart area, ignore
    if (px < 0 || py < 0 || px > innerWidth || py > innerHeight) return;

    const hitIdx = hitTestPoint(px, py);
    if (hitIdx >= 0) {
      // delete non-edge points
      setPointsData(prev => {
        if (hitIdx === 0 || hitIdx === prev.length - 1) return prev;
        const next = prev.filter((_, i) => i !== hitIdx);
        onChange?.(next);
        return next;
      });
      return;
    }

    // add point into the segment
    const m = clamp(xScale.invert(px), 0, 1440);
    const next = insertPointAt(m);
    setPointsData(next);
    onChange?.(next);
  };

  const applyMove = (index: number, px: number, py: number) => {
    setPointsData(prev => {
      const next = prev.slice();
      const proposedM = clamp(xScale.invert(px), 0, 1440);
      const proposedV = clamp(yScale.invert(py), 0, 100);

      // Snap time to 10-minute steps
      const snap = (m: number, step: number) => Math.round(m / step) * step;
      const snappedM = snap(proposedM, 10);

      if (index === 0) {
        // Keep first point fixed at 00:00 by time, only value changes
        next[0] = { m: 0, v: proposedV };
      } else if (index === next.length - 1) {
        // Keep last point fixed at 24:00 by time, only value changes
        next[next.length - 1] = { m: 1440, v: proposedV };
      } else {
        const left = next[index - 1].m;
        const right = next[index + 1].m;
        // Prevent crossing neighbours, but apply snapping first
        const mSnapped = clamp(snappedM, left + 1e-3, right - 1e-3);
        next[index] = { m: mSnapped, v: proposedV };
      }
      onChange?.(next);
      return next;
    });
  };

  return (
    <div ref={containerRef} className="w-full mx-auto" style={{ maxWidth: 1200 }}>
      <svg ref={svgRef} width={svgWidth} height={svgHeight} className="rounded-2xl shadow ring-1 ring-gray-200 bg-white" style={{ display: 'block', width: '100%', height: 'auto' }}>
        <defs>
          <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0.05} />
          </linearGradient>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g transform={`translate(${margin.left},${margin.top})`} onDoubleClick={handleDblClick} style={{ cursor: 'crosshair' }}>
          <GridRows scale={yScale} width={innerWidth} stroke="#eef2f7" strokeDasharray="4 4" pointerEvents="none" numTicks={10} />
          <GridColumns scale={xScale} height={innerHeight} stroke="#eef2f7" strokeDasharray="4 4" pointerEvents="none" tickValues={hourTicks} />

          {area && <path d={area} fill="url(#areaFill)" stroke="none" />}
          {line && <path d={line} fill="none" stroke={color} strokeWidth={3} filter="url(#glow)" />}

          {pathPoints.map((p, i) => (
            <Handle
              key={`h-${i}`}
              index={i}
              cx={p.x}
              cy={p.y}
              svgRef={svgRef}
              marginLeft={margin.left}
              marginTop={margin.top}
              selected={selectedIdx === i}
              hovered={hoverIdx === i}
              onHoverChange={(on) => setHoverIdx(on ? i : hoverIdx === i ? null : hoverIdx)}
              onSelect={() => setSelectedIdx(i)}
              onMoveLocal={(mx, my) => applyMove(i, mx, my)}
              label={`${minutesToHM(pointsData[i].m)} · ${Math.round(pointsData[i].v)}%`}
            />
          ))}

          <AxisLeft
            scale={yScale}
            numTicks={6}
            tickFormat={(v: NumberValue) => `${Number(v)}%`}
            tickStroke="#cbd5e1"
            stroke="#cbd5e1"
            tickLabelProps={() => ({ fill: "#475569", fontSize: 12, textAnchor: "end", dy: "0.33em" })}
          />
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            tickValues={hourTicks}
            tickFormat={(v: NumberValue) => `${Math.floor(Number(v) / 60)}`}
            tickStroke="#cbd5e1"
            stroke="#cbd5e1"
            tickLabelProps={() => ({ fill: "#475569", fontSize: 12, textAnchor: "middle", dy: "0.25em" })}
          />
        </g>
      </svg>
    </div>
  );
};

type HandleProps = {
  index: number;
  cx: number;
  cy: number;
  svgRef: React.RefObject<SVGSVGElement>;
  marginLeft: number;
  marginTop: number;
  selected: boolean;
  hovered: boolean;
  onHoverChange: (on: boolean) => void;
  onSelect: () => void;
  onMoveLocal: (mx: number, my: number) => void;
  label: string;
};

const Handle = ({ index, cx, cy, svgRef, marginLeft, marginTop, selected, hovered, onHoverChange, onSelect, onMoveLocal, label }: HandleProps) => {
  const [dragging, setDragging] = useState(false);

  const pointerDownInner = (e: React.PointerEvent<SVGCircleElement>) => {
    e.preventDefault();
    const svg = svgRef.current!;
    (e.currentTarget as any).setPointerCapture?.(e.pointerId);
    setDragging(true);
    onSelect();

    const move = (ev: PointerEvent) => {
      const rect = svg.getBoundingClientRect();
      const px = ev.clientX - rect.left - marginLeft;
      const py = ev.clientY - rect.top - marginTop;
      onMoveLocal(px, py);
    };

    const up = () => {
      setDragging(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const tooltipWidth = Math.max(84, label.length * 6.6);

  return (
    <g
      transform={`translate(${cx},${cy})`}
      style={{ cursor: dragging ? "grabbing" : "grab" }}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      {hovered && (
        <g transform={`translate(0,-26)`}>
          <rect
            x={-tooltipWidth / 2}
            y={-16}
            width={tooltipWidth}
            height={20}
            rx={6}
            fill="#ffffff"
            stroke="#cbd5e1"
          />
          <text x={0} y={-3} textAnchor="middle" fill="#111827" style={{ fontSize: 11 }}>
            {label}
          </text>
        </g>
      )}

      <circle
        r={selected ? 8.5 : 7.5}
        fill="#ffffff"
        stroke="#0ea5e9"
        onPointerDown={pointerDownInner}
      />
      <circle r={2.2} fill="#0ea5e9" pointerEvents="none" />
    </g>
  );
};

